import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accessGroupsService,
  AccessGroup,
  AccessGroupIntegrationInput,
} from '@/services/access-groups.service';

export interface AccessGroupFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface AccessGroupMemberFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export const accessGroupKeys = {
  all: ['access-groups'] as const,
  lists: () => [...accessGroupKeys.all, 'list'] as const,
  list: (filters: AccessGroupFilters) =>
    [...accessGroupKeys.lists(), filters] as const,
  details: () => [...accessGroupKeys.all, 'detail'] as const,
  detail: (id: string) => [...accessGroupKeys.details(), id] as const,
  availableProducts: (id: string) =>
    [...accessGroupKeys.all, 'available-products', id] as const,
  byProduct: (productId: string) =>
    [...accessGroupKeys.all, 'by-product', productId] as const,
  members: (id: string, filters: AccessGroupMemberFilters) =>
    [...accessGroupKeys.all, 'members', id, filters] as const,
  integrations: (id: string) =>
    [...accessGroupKeys.all, 'integrations', id] as const,
};

// Admin queries
export function useAccessGroups(filters: AccessGroupFilters = {}) {
  return useQuery({
    queryKey: accessGroupKeys.list(filters),
    queryFn: () => accessGroupsService.findAll(filters),
  });
}

export function useAccessGroup(id: string, enabled = true) {
  return useQuery({
    queryKey: accessGroupKeys.detail(id),
    queryFn: () => accessGroupsService.findById(id),
    enabled: enabled && !!id,
  });
}

export function useAvailableProducts(groupId: string, enabled = true) {
  return useQuery({
    queryKey: accessGroupKeys.availableProducts(groupId),
    queryFn: () => accessGroupsService.getAvailableProducts(groupId),
    enabled: enabled && !!groupId,
  });
}

export function useAccessGroupsByProduct(productId: string, enabled = true) {
  return useQuery({
    queryKey: accessGroupKeys.byProduct(productId),
    queryFn: () => accessGroupsService.findByProductId(productId),
    enabled: enabled && !!productId,
  });
}

export function useAccessGroupMembers(
  id: string,
  filters: AccessGroupMemberFilters = {},
  enabled = true,
) {
  return useQuery({
    queryKey: accessGroupKeys.members(id, filters),
    queryFn: () => accessGroupsService.getMembers(id, filters),
    enabled: enabled && !!id,
  });
}

export function useAccessGroupIntegrations(id: string, enabled = true) {
  return useQuery({
    queryKey: accessGroupKeys.integrations(id),
    queryFn: () => accessGroupsService.getIntegrations(id),
    enabled: enabled && !!id,
  });
}

export function useCreateAccessGroupIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: string;
      data: AccessGroupIntegrationInput;
    }) => accessGroupsService.createIntegration(groupId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.integrations(variables.groupId),
      });
    },
  });
}

export function useUpdateAccessGroupIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      integrationId,
      data,
    }: {
      groupId: string;
      integrationId: string;
      data: AccessGroupIntegrationInput;
    }) => accessGroupsService.updateIntegration(groupId, integrationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.integrations(variables.groupId),
      });
    },
  });
}

export function useDeleteAccessGroupIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      integrationId,
    }: {
      groupId: string;
      integrationId: string;
    }) => accessGroupsService.deleteIntegration(groupId, integrationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.integrations(variables.groupId),
      });
    },
  });
}

// Mutations
export function useCreateAccessGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      accessDuration?: number;
      accessType?: string;
      expirationDate?: string;
      isActive?: boolean;
      termsTitle?: string;
      termsFileUrl?: string;
      productIds?: string[];
    }) => accessGroupsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
    },
  });
}

export function useUpdateAccessGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        title?: string;
        description?: string;
        accessDuration?: number;
        accessType?: string;
        expirationDate?: string;
        isActive?: boolean;
        termsTitle?: string;
        termsFileUrl?: string;
        productIds?: string[];
      };
    }) => accessGroupsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail(variables.id),
      });
    },
  });
}

export function useDeleteAccessGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accessGroupsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
    },
  });
}

export function useDuplicateAccessGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accessGroupsService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
    },
  });
}

export function useAddProductsToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      productIds,
    }: {
      groupId: string;
      productIds: string[];
    }) => accessGroupsService.addProducts(groupId, productIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.availableProducts(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
    },
  });
}

export function useRemoveProductsFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      productIds,
    }: {
      groupId: string;
      productIds: string[];
    }) => accessGroupsService.removeProducts(groupId, productIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.detail(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: accessGroupKeys.availableProducts(variables.groupId),
      });
      queryClient.invalidateQueries({ queryKey: accessGroupKeys.lists() });
    },
  });
}

export type { AccessGroup };
